import {useCallback, useEffect, useState} from 'react';

import {ReadOnlyHookReturn} from 'src/typedef';

import {useAbortSignal} from '@pb/kit-hooks-use-abort-signal';

import type {Error as ApiError} from 'src/openapi/admin/models/Error';


type DispatchResponse<T> = {
    type: string,
    payload: {
        data: T,
        success: true,
        error: false,
    } | {
        data: ApiError,
        success: false,
        error: true,
    },
};

type Props<Data, > = {
    initValue: Data,
    getDispatchAction: (signal: AbortSignal) => Promise<DispatchResponse<Data>>,
    shouldLoadData?: boolean,
};

export const useReadOnlyDataHook = <Data, >(params: Props<Data>): ReadOnlyHookReturn<Data, ApiError | null> => {
    const {getDispatchAction, shouldLoadData = true, initValue} = params;
    const {createAbortSignal} = useAbortSignal();

    const [data, setData] = useState<Data>(initValue as Data);
    const [loadingError, setLoadingError] = useState<ApiError | null>(null);
    const [isLoading, setIsLoading] = useState(shouldLoadData);

    const loadData = useCallback(async () => {
        setIsLoading(true);
        const signal = createAbortSignal();
        try {
            const res = await getDispatchAction(signal);
            if (res.payload.success) {
                setData(res.payload.data);
                setLoadingError(null);
            } else if (res.payload.error) {
                setLoadingError(res.payload.data as ApiError);
            }
        } catch (err) {
            if ((err as {name?: string})?.name !== 'AbortError') {
                setLoadingError((err as ApiError) ?? null);
            }
        } finally {
            setIsLoading(false);
        }
    }, [getDispatchAction, createAbortSignal]);

    useEffect(() => {
        if (shouldLoadData) {
            loadData();
        }
    }, [shouldLoadData, loadData]);

    return {
        isLoading,
        data,
        loadData,
        loadingError,
        createAbortSignal,
    };
};
